import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type DependencyList,
} from 'react';
import {
  loaderStore,
  localLoaderKey,
  startPoll,
  type LoadFn,
  type LoaderSnapshot,
} from './loader-store';

export { clearLoaderCache, invalidateLoaders, prefetchLoader } from './loader-store';
export type { LoadFn } from './loader-store';

/** Element-wise `Object.is`, like React's own dependency comparison. */
export function sameDeps(a: DependencyList, b: DependencyList): boolean {
  return a.length === b.length && a.every((value, i) => Object.is(value, b[i]));
}

/**
 * Numbers a component's requests so only the newest may set state, for a list that loads from
 * more than one place (a first-page effect and the pager) where an effect's own `cancelled` flag
 * can't see the other. `const [startRequest] = useState(createLatestRequest)`, then
 * `const isLatest = startRequest()` as a request starts and `if (!isLatest()) return` when it answers.
 */
export function createLatestRequest(): () => () => boolean {
  let latest = 0;
  return () => {
    const request = ++latest;
    return () => request === latest;
  };
}

export type Loader<T> = {
  /** The latest result. While a new load runs, the previous result stays (no flash of empty). */
  data: T | undefined;
  /** A rejected load for the current inputs (API helpers that return `{ error }` resolve instead). */
  error: unknown;
  /**
   * True until a load for the current inputs has finished (and again after `reload()`). `data` may
   * still hold the previous inputs' result meanwhile: `loading && !refreshing` means nothing to show.
   */
  loading: boolean;
  /**
   * A newer result is on its way while `data` is on screen: other inputs loading behind the last
   * ones, a refresh, a background revalidation or a poll. Show it subtly (DataTable `refreshing`);
   * never blank what's shown.
   */
  refreshing: boolean;
  /** When `data` came from the server (epoch ms), for an "as of HH:MM" note. */
  updatedAt: number | undefined;
  /** Load again with the same inputs (after an edit elsewhere). `loading` stays true until it lands. */
  reload: () => void;
  /**
   * Load again now and wait for it; `loading` does not change (for "Refresh" buttons with their own
   * spinner). A result for inputs that changed meanwhile is not shown.
   */
  refresh: () => Promise<T>;
  /** Change the loaded data locally, e.g. after saving one row. */
  setData: (update: T | ((current: T | undefined) => T)) => void;
};

export type LoaderOptions<T> = {
  /** False to wait (e.g. until a required filter is chosen): nothing loads, `loading` is false. */
  enabled?: boolean;
  /**
   * Share and cache the result under this key, starting with the project id
   * (`${projectId}:runs:${query}`). The key is the identity: it must change whenever
   * anything the loader reads changes (`deps` are then not compared). Coming back to a cached key
   * shows its data at once and loads again behind it once older than `staleMs`.
   */
  key?: string;
  /** How long a cached result counts as fresh when a loader mounts with its key (default 0). */
  staleMs?: number;
  /** Load again every `pollMs` after the last answer, paused while the tab is hidden. */
  pollMs?: number;
  /** Keep polling only while this says so (`(job) => job?.status === 'running'`). */
  pollWhile?: (data: T | undefined) => boolean;
  /**
   * Stop after this many polls in a row, for work that may never finish (a picture that stays
   * undescribed). Counted afresh for new inputs, and when `pollWhile` stops and starts again.
   */
  pollLimit?: number;
};

type Identity<T> = {
  deps: DependencyList;
  key: string | undefined;
  version: number;
  /** What was on screen when the inputs last changed, shown until the new inputs have data. */
  placeholder: T | undefined;
};

/**
 * Load data when its inputs change. `deps` work like an effect's: list everything `load` reads.
 * `load` gets an AbortSignal; pass it to fetchApi (`{ signal }`) so a superseded request stops.
 * Only the newest request's answer is ever shown, whatever order answers arrive in.
 *
 * ```ts
 * const { data, loading, refreshing, reload } = useLoader(
 *   (signal) => getThings(projectId, page, { signal }),
 *   [projectId, page]
 * );
 * ```
 *
 * With `key`, loaders share one cached result (`invalidateLoaders(prefix)` after a save).
 */
export function useLoader<T>(
  load: LoadFn<T>,
  deps: DependencyList,
  options: LoaderOptions<T> = {}
): Loader<T> {
  const { enabled = true, key: sharedKey, staleMs = 0, pollMs, pollWhile, pollLimit } = options;
  const instance = useId();
  const [identity, setIdentity] = useState<Identity<T>>(() => ({
    deps,
    key: sharedKey,
    version: 0,
    placeholder: undefined,
  }));
  const key = identity.key ?? localLoaderKey(instance, identity.version);

  const subscribe = useCallback((listener: () => void) => loaderStore.listen(key, listener), [key]);
  const getSnapshot = useCallback(() => loaderStore.snapshot<T>(key), [key]);
  const snapshot: LoaderSnapshot<T> = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const data = snapshot.hasData ? snapshot.data : identity.placeholder;

  // New inputs: a new key (React's "adjust state when a prop changes"), keeping what's on screen.
  const inputsChanged =
    sharedKey !== identity.key || (sharedKey === undefined && !sameDeps(deps, identity.deps));
  if (inputsChanged) {
    setIdentity({ deps, key: sharedKey, version: identity.version + 1, placeholder: data });
  }

  // The latest loader, key and switches, for requests started later (reload, refresh, invalidate,
  // poll) and for callbacks held from an earlier render: they act on the inputs shown now.
  const latest = useRef({ load, key, enabled, placeholder: identity.placeholder });
  useLayoutEffect(() => {
    latest.current = { load, key, enabled, placeholder: identity.placeholder };
  });

  useEffect(() => {
    if (!enabled) return undefined;
    // Only while this hook's inputs are still this key: a loader for other inputs never loads it.
    const getLoad = () =>
      latest.current.key === key ? (latest.current.load as LoadFn<unknown>) : null;
    return loaderStore.use(key, getLoad, { staleMs });
  }, [key, enabled, staleMs]);

  const wantsPoll =
    enabled && pollMs !== undefined && pollMs > 0 && (pollWhile ? pollWhile(data) : true);
  // Polls in a row for `pollLimit`: counted afresh for new inputs and when polling stops.
  const counting = pollLimit !== undefined;
  const [polls, setPolls] = useState({ key, count: 0 });
  const pollCount = polls.key === key ? polls.count : 0;
  if (counting && pollCount > 0 && !wantsPoll) setPolls({ key, count: 0 });
  const keepPolling = wantsPoll && (!counting || pollCount < pollLimit);
  const { fetching, settledAt } = snapshot;
  useEffect(() => {
    if (!keepPolling || fetching || pollMs === undefined) return undefined;
    return startPoll({
      pollMs,
      lastAt: settledAt,
      run: () => {
        if (counting) {
          setPolls((p) => ({ key, count: (p.key === key ? p.count : 0) + 1 }));
        }
        loaderStore.fetch(key, 'background').catch(() => undefined);
      },
    });
  }, [keepPolling, fetching, settledAt, pollMs, key, counting]);

  const reload = useCallback(() => {
    const now = latest.current;
    if (now.enabled) loaderStore.fetch(now.key, 'reload').catch(() => undefined);
  }, []);
  const refresh = useCallback((): Promise<T> => {
    const now = latest.current;
    return now.enabled
      ? loaderStore.fetch<T>(now.key, 'refresh')
      : now.load(new AbortController().signal);
  }, []);
  const setData = useCallback((update: T | ((current: T | undefined) => T)) => {
    const now = latest.current;
    loaderStore.setData<T>(now.key, update, now.placeholder);
  }, []);

  const settled = snapshot.settled;
  return {
    data,
    error: settled ? snapshot.error : undefined,
    loading: enabled && !settled,
    refreshing: enabled && data !== undefined && (snapshot.fetching || !settled),
    updatedAt: snapshot.hasData ? snapshot.updatedAt : undefined,
    reload,
    refresh,
    setData,
  };
}
